import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import router from "../router/index.js";
import biomaxRoutes from "../router/biomaxRoutes.js";
import trafiguraRoutes, {
  isTrafiguraStaticRoute,
} from "../router/trafiguraRoutes.js";
import comercialRoutes, {
  isComercialStaticRoute,
} from "../router/comercialRoutes.js";
import simulacrosRoutes from "../router/simulacrosRoutes.js";

Vue.use(Vuex);

import modSeguridad from "./modSeguridad/index";
import modBiomax from "./modBiomax/index";
import modTrafigura from "./modTrafigura/index";
import axios from "axios";
import { parseLoginError } from "../utils/loginErrors";

function resolveApiBaseUrl() {
  const configured = (process.env.VUE_APP_API_URL || "").trim();
  const isDev = process.env.NODE_ENV === "development";
  const isLocalBackend =
    !configured ||
    /^https?:\/\/(localhost|127\.0\.0\.1):8000\/?$/i.test(configured);

  // En dev contra back local: rutas absolutas desde raíz → proxy de vue.config.js.
  if (isDev && isLocalBackend) {
    return "/";
  }

  return configured;
}

/** Evita /Seguridad/api/... cuando la URL no lleva / inicial (ruta relativa al path actual). */
function normalizeAxiosUrl(url) {
  if (!url || /^https?:\/\//i.test(url) || url.charAt(0) === "/") {
    return url;
  }

  return `/${url}`;
}

axios.defaults.withCredentials = true;
// axios >=1.6 dejó de enviar X-XSRF-TOKEN en cross-origin (new.* → backnew.*).
axios.defaults.withXSRFToken = true;
axios.defaults.baseURL = resolveApiBaseUrl();
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
axios.defaults.headers.common["Accept"] = "application/json";

axios.interceptors.request.use((config) => {
  if (config.url) {
    config.url = normalizeAxiosUrl(config.url);
  }

  return config;
});

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Manejar el error aquí
    if (error.response) {
      // La solicitud fue realizada, pero el servidor respondió con un código de estado fuera del rango 2xx

      const status = error.response.status;

      if (status === 401) {
        let excludedRoutes = [
          "LoginForm",
          "TwoFactorChallenge",
          "ForgotPassword",
          "ResetPassword",
        ];

        // Obtener la ruta actual
        let currentPath = router.currentRoute.path;

        // Si la ruta actual es null o undefined, asignarle el valor de "/"
        if (!currentPath) {
          currentPath = "/";
          excludedRoutes.push("/");
        }

        if (currentPath == "/") {
          excludedRoutes.push("/");
        } else {
          excludedRoutes = excludedRoutes.filter((r) => r !== "/");
        }

        const enPantallaLogin = excludedRoutes.some((ruta) =>
          currentPath.includes(ruta)
        );

        // Verificar si la ruta actual está en las rutas excluidas
        if (!enPantallaLogin) {
          alert("Su sesión ha caducado, por favor vuelva a ingresar");
          window.location.href = "/LoginForm";
        }
      }
    } else if (error.request) {
      // La solicitud fue realizada pero no se recibió respuesta
      //console.error('Request:', error.request);
    } else {
      // Algo sucedió en la configuración de la solicitud que desencadenó un error
      //console.error('Error:', error.message);
    }
    //console.error('Config:', error.config);

    // Retornar una promesa rechazada
    return Promise.reject(error);
  }
);

export default new Vuex.Store({
  plugins: [createPersistedState()],
  state: {
    user: null,
    errors: null,
    mensaje_login: null,
    auth: false,
    pendingTwoFactor: false,
    pendingTwoFactorChannels: { app: false, email: false },
    permissions: [],
    menus: [],
    routes: [],
    accessToken: "",
    currentLineaNegocioId: null, // Guardar la línea de negocio seleccionada
    currentClientePlataformaId: null, // Cliente plataforma seleccionado (primer menú)
    lineasParaClienteActual: [], // Líneas de negocio del cliente elegido (para panel Home, evita que no renderice)
    darkMode: false, // Tema claro (false) / oscuro (true). Persistido con vuex-persistedstate
    /** Evita bucle Home ↔ módulo cuando hay cliente/línea pero sin rol o sin permisos (menú vacío o sin acceso al dashboard). */
    entradaModuloFallida: null, // null | { lineaNegocioId, clientePlataformaId }
    /** Incrementado al volver a elegir cliente (navbar / Home); HomeView resetea estado local. */
    homeClienteSeleccionResetNonce: 0,
  },
  mutations: {
    setAccessToken(state, token) {
      state.accessToken = token;
    },
    SET_USER(state, user) {
      state.user = user;
      state.auth = Boolean(user);
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },
    SET_MENUS(state, menus) {
      state.menus = menus;
    },
    SET_ROUTES(state, routes) {
      state.routes = routes;
    },
    SET_ERRORS(state, error) {
      state.errors = error;
    },
    SET_MENSAJE_LOGIN(state, mensaje) {
      state.mensaje_login = mensaje;
    },
    SET_PENDING_TWO_FACTOR(state, value) {
      state.pendingTwoFactor = Boolean(value);
    },
    SET_PENDING_TWO_FACTOR_CHANNELS(state, channels) {
      state.pendingTwoFactorChannels = {
        app: Boolean(channels && channels.app),
        email: Boolean(channels && channels.email),
      };
    },
    SET_CURRENT_LINEA_NEGOCIO_ID(state, linea_negocio_id) {
      state.currentLineaNegocioId = linea_negocio_id;
    },
    SET_CURRENT_CLIENTE_PLATAFORMA_ID(state, cliente_plataforma_id) {
      state.currentClientePlataformaId = cliente_plataforma_id;
    },
    SET_LINEAS_PARA_CLIENTE_ACTUAL(state, lineas) {
      state.lineasParaClienteActual = Array.isArray(lineas) ? lineas : [];
    },
    SET_DARK_MODE(state, value) {
      state.darkMode = Boolean(value);
    },
    SET_ENTRADA_MODULO_FALLIDA(state, payload) {
      state.entradaModuloFallida =
        payload && payload.lineaNegocioId != null ? { ...payload } : null;
    },
    CLEAR_ENTRADA_MODULO_FALLIDA(state) {
      state.entradaModuloFallida = null;
    },
    BUMP_HOME_CLIENTE_RESET(state) {
      state.homeClienteSeleccionResetNonce += 1;
    },
  },
  actions: {
    async completeAuthenticatedSession({ dispatch, commit }) {
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);
      commit("SET_PENDING_TWO_FACTOR", false);
      commit("SET_PENDING_TWO_FACTOR_CHANNELS", { app: false, email: false });
      commit("CLEAR_ENTRADA_MODULO_FALLIDA");

      try {
        await dispatch("getUser", { required: true });
        await dispatch("getPermissions", { required: true });
      } catch (e) {
        const parsed = parseLoginError(e);
        commit("SET_USER", null);
        commit(
          "SET_ERRORS",
          parsed.code === "unknown" ? "session_load_failed" : parsed.code
        );
        commit(
          "SET_MENSAJE_LOGIN",
          parsed.code === "unknown"
            ? "El inicio de sesión fue exitoso, pero no pudimos cargar tu perfil. Recarga la página e inténtalo de nuevo."
            : parsed.message
        );
        return false;
      }

      try {
        await dispatch("getMenus");
        await dispatch("getRoutes", this.getters.getMenus);
        await dispatch("setRoutes");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[login] No se pudieron cargar menús/rutas iniciales:", e);
      }

      return true;
    },

    async login({ dispatch, commit }, credentials) {
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);
      commit("SET_PENDING_TWO_FACTOR", false);
      commit("SET_PENDING_TWO_FACTOR_CHANNELS", { app: false, email: false });
      commit("CLEAR_ENTRADA_MODULO_FALLIDA");

      // 1) CSRF cookie (Sanctum). Si esto falla por red/CORS, se reporta y se aborta.
      try {
        await axios.get("/sanctum/csrf-cookie");
      } catch (e) {
        const parsed = parseLoginError(e);
        commit("SET_USER", null);
        commit("SET_ERRORS", parsed.code);
        commit("SET_MENSAJE_LOGIN", parsed.message);
        return false;
      }

      // 2) POST /login (no seguir redirect 302 a /home del back — es ruta Laravel, no Vue).
      try {
        const payload = {
          email: credentials && credentials.email,
          password: credentials && credentials.password,
          remember: Boolean(credentials && credentials.remember),
        };
        const loginRes = await axios.post("/login", payload, {
          maxRedirects: 0,
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 302,
        });
        const loginStatus = loginRes && loginRes.status;
        if (loginStatus === 302) {
          const loc = String(
            (loginRes.headers && (loginRes.headers.location || loginRes.headers.Location)) || ""
          );
          if (loc && !loc.includes("/home")) {
            const err = new Error("login_redirect_unexpected");
            err.response = loginRes;
            throw err;
          }
        }

        if (loginRes.data && loginRes.data.two_factor === true) {
          commit("SET_USER", null);
          commit("SET_PENDING_TWO_FACTOR", true);
          commit(
            "SET_PENDING_TWO_FACTOR_CHANNELS",
            (loginRes.data && loginRes.data.channels) || { app: true, email: false }
          );
          return "two_factor";
        }
      } catch (error) {
        const parsed = parseLoginError(error);
        commit("SET_USER", null);
        commit("SET_ERRORS", parsed.code);
        commit("SET_MENSAJE_LOGIN", parsed.message);
        return false;
      }

      return dispatch("completeAuthenticatedSession");
    },

    async verifyTwoFactor({ dispatch, commit }, payload) {
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);

      const body = {};
      if (payload && payload.recovery_code) {
        body.recovery_code = String(payload.recovery_code).trim();
      } else if (payload && payload.code) {
        body.code = String(payload.code).trim();
      }

      try {
        await axios.post("/two-factor-challenge", body);
      } catch (error) {
        const parsed = parseLoginError(error);
        commit("SET_USER", null);
        commit("SET_ERRORS", parsed.code);
        commit("SET_MENSAJE_LOGIN", parsed.message);
        return false;
      }

      return dispatch("completeAuthenticatedSession");
    },

    async sendTwoFactorEmail({ commit }) {
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);
      try {
        const { data } = await axios.post("/two-factor-email/send");
        return data || { message: "Código enviado." };
      } catch (error) {
        const parsed = parseLoginError(error);
        commit("SET_ERRORS", parsed.code);
        commit("SET_MENSAJE_LOGIN", parsed.message);
        throw error;
      }
    },

    async verifyTwoFactorEmail({ dispatch, commit }, payload) {
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);
      try {
        await axios.post("/two-factor-email/verify", {
          code: String((payload && payload.code) || "").trim(),
        });
      } catch (error) {
        const parsed = parseLoginError(error);
        commit("SET_USER", null);
        commit("SET_ERRORS", parsed.code);
        commit("SET_MENSAJE_LOGIN", parsed.message);
        return false;
      }
      return dispatch("completeAuthenticatedSession");
    },

    async getUser({ commit }, options = {}) {
      const required = Boolean(options && options.required);
      try {
        const res = await axios.get("/api/user");
        if (!res.data || !res.data.id) {
          const err = new Error("user_empty");
          err.code = "user_empty";
          throw err;
        }
        commit("SET_USER", res.data);
        return res.data;
      } catch (e) {
        commit("SET_USER", null);
        if (required) {
          throw e;
        }
      }
    },

    setDarkMode({ commit }, value) {
      commit("SET_DARK_MODE", value);
    },

    /** Volver a la pantalla de elegir cliente plataforma (solo debe usarse si el usuario tiene más de un cliente). */
    prepararCambioDeClientePlataforma({ commit }) {
      commit("CLEAR_ENTRADA_MODULO_FALLIDA");
      commit("SET_LINEAS_PARA_CLIENTE_ACTUAL", []);
      commit("SET_CURRENT_CLIENTE_PLATAFORMA_ID", null);
      commit("SET_CURRENT_LINEA_NEGOCIO_ID", null);
      commit("SET_MENUS", []);
      commit("SET_ROUTES", []);
      commit("BUMP_HOME_CLIENTE_RESET");
    },

    async logout({ dispatch, commit }) {
      await axios.post("/logout");
      commit("SET_PENDING_TWO_FACTOR", false);
      commit("CLEAR_ENTRADA_MODULO_FALLIDA");
      await dispatch("getMenus", null);
      await dispatch("getPermissions");
      await dispatch("getUser");
      // Limpiar la línea de negocio y cliente plataforma al hacer logout
      commit("SET_CURRENT_LINEA_NEGOCIO_ID", null);
      commit("SET_CURRENT_CLIENTE_PLATAFORMA_ID", null);
    },

    async getPermissions({ commit }, options = {}) {
      const required = Boolean(options && options.required);
      try {
        const res = await axios.get("/api/user/permissions");
        commit("SET_PERMISSIONS", res.data);
        return res.data;
      } catch (e) {
        commit("SET_PERMISSIONS", []);
        if (required) {
          throw e;
        }
      }
    },

    async getMenus({ commit }, linea_negocio_id) {
      await axios
        .get("/api/menu", { params: { linea_negocio_id: linea_negocio_id } })
        .then((response) => {
          commit("SET_MENUS", response.data);
          // Guardar la línea de negocio seleccionada
          if (linea_negocio_id) {
            commit("SET_CURRENT_LINEA_NEGOCIO_ID", linea_negocio_id);
          }
        })
        .catch(() => {
          commit("SET_MENUS", []);
        });
    },

    async getRoutes({ commit }, menus) {
      var r = [];
      if (Array.isArray(menus)) {
        menus.forEach((menu) => {
          const nivel2 = menu?.nivel2;
          if (Array.isArray(nivel2)) {
            nivel2.forEach((nivel2Item) => {
              const rutas = nivel2Item?.rutas;
              if (Array.isArray(rutas)) {
                rutas.forEach((ruta) => {
                  if (ruta?.path) r.push(ruta);
                });
              }
            });
          }
        });
      }
      commit("SET_ROUTES", r);
    },

    async setRoutes() {
      this.dispatch("addBiomaxSeguridadRoutes");
      this.dispatch("addTrafiguraStaticRoutes");
      this.dispatch("addComercialStaticRoutes");
      this.dispatch("addSimulacrosStaticRoutes");
      const seenPaths = new Set();
      const fromApi = this.getters.getRoutes || [];
      fromApi.forEach((dataRoutes) => {
        if (!dataRoutes || dataRoutes.path == null || dataRoutes.path === "") return;
        let path = String(dataRoutes.path).trim();
        if (!path.startsWith("/")) path = "/" + path.replace(/^\/+/, "");
        if (seenPaths.has(path)) return;
        if (isTrafiguraStaticRoute(path)) return;
        if (isComercialStaticRoute(path)) return;
        const comp = dataRoutes.component;
        if (comp == null || String(comp).trim() === "") return;
        seenPaths.add(path);
        router.addRoutes([
          {
            path,
            name: dataRoutes.name,
            component: () => import("../views/" + String(comp).trim() + ".vue"),
          },
        ]);
      });
      this.dispatch("addDefaultSeguridadPorClienteRoutes");
    },

    /** Ruta por defecto para línea de negocio 6 (Seguridad) cuando el menú no devuelve rutas */
    addDefaultSeguridadRoute() {
      const rutaDefault = {
        path: "/Sos/Admin/Seguridad/Usuarios",
        name: "/Sos/Admin/Seguridad/Usuarios",
        component: () => import("../views/sos/admin/seguridad/usuarios/UsuarioIndex.vue"),
      };
      router.addRoutes([rutaDefault]);
    },

    /** Rutas de Seguridad por cliente (usuarios, permisos, roles dependientes del cliente actual) */
    addDefaultSeguridadPorClienteRoutes() {
      const rutas = [
        { path: "/Seguridad/Cliente/Usuarios", name: "SeguridadClienteUsuarios", component: "seguridad/por-cliente/usuarios/UsuarioIndexPorCliente" },
        { path: "/Seguridad/Cliente/Permisos", name: "SeguridadClientePermisos", component: "seguridad/por-cliente/permisos/PermisoIndexPorCliente" },
        { path: "/Seguridad/Cliente/Roles", name: "SeguridadClienteRoles", component: "seguridad/por-cliente/roles/RolIndexPorCliente" },
      ];
      rutas.forEach((r) => {
        router.addRoutes([{
          path: r.path,
          name: r.name,
          component: () => import("../views/" + r.component + ".vue"),
        }]);
      });
    },

    /** Rutas estáticas Biomax (router/biomaxRoutes.js). */
    addBiomaxSeguridadRoutes() {
      router.addRoutes(biomaxRoutes);
    },

    /** Rutas Trafigura que no deben venir del menú API (evita admin/rutas + query plataforma). */
    addTrafiguraStaticRoutes() {
      router.addRoutes(trafiguraRoutes);
    },

    /** Rutas estáticas Comercial (router/comercialRoutes.js). */
    addComercialStaticRoutes() {
      router.addRoutes(comercialRoutes);
    },

    addSimulacrosStaticRoutes() {
      router.addRoutes(simulacrosRoutes);
    },
  },
  getters: {
    isAuthenticated: (state) => {
      return state.auth;
    },
    /** Usuario autenticado sin ningún canal 2FA (app/correo). */
    requiresTwoFactorSetup: (state, getters) => {
      if (!getters.isAuthenticated || !state.user) {
        return false;
      }
      return state.user.two_factor_enabled !== true;
    },
    getPermissions: (state) => {
      return state.permissions;
    },
    getRoutes: (state) => {
      return state.routes;
    },
    getMenus: (state) => {
      return state.menus;
    },
    getUser: (state) => {
      return state.user;
    },
    getErrors: (state) => {
      return state.errors;
    },
    getMensajeLogin: (state) => {
      return state.mensaje_login;
    },
    pendingTwoFactor: (state) => state.pendingTwoFactor,
    pendingTwoFactorChannels: (state) =>
      state.pendingTwoFactorChannels || { app: false, email: false },
    getCurrentLineaNegocioId: (state) => {
      return state.currentLineaNegocioId;
    },
    getCurrentClientePlataformaId: (state) => {
      return state.currentClientePlataformaId;
    },
    getLineasParaClienteActual: (state) => {
      return state.lineasParaClienteActual || [];
    },
    getDarkMode: (state) => state.darkMode,

    can: (state) => (permissionName) => {
      const perms = state.permissions || [];
      if (!permissionName) return false;
      if (perms.find((per) => per.name === permissionName) !== undefined) {
        return true;
      }
      const clientes =
        (state.user &&
          (state.user.clientes_plataforma || state.user.clientesPlataforma)) ||
        [];
      const cliente = clientes.find(
        (c) => Number(c.id) === Number(state.currentClientePlataformaId)
      );
      const codigo =
        (cliente && (cliente.codigo_menu || cliente.codigoMenu)) ||
        "";
      if (!codigo) return false;
      const clientePrefix = `Cliente(${codigo}).`;
      const codigoPrefix = `${codigo}.`;
      return (
        perms.find((per) => {
          const n = per && per.name;
          if (!n) return false;
          return (
            n === codigoPrefix + permissionName ||
            n === clientePrefix + permissionName ||
            (n.indexOf(clientePrefix) === 0 &&
              n.endsWith("." + permissionName))
          );
        }) !== undefined
      );
    },
  },
  modules: {
    modSeguridad: modSeguridad,
    modBiomax: modBiomax,
    modTrafigura: modTrafigura,
  },
});
